<template>
  <div id="leaf-relief" class="wwf-form donation-form leaf-relief-form">
    <!-- Header -->
    <LeafReliefHeader
      :datalayerName="datalayerName"
      :counterLimit="counterLimit"
      :targetTitle="targetTitle"
      :targetText="targetText"
      :headerText="headerText"
      :showIcon="showIcon"
    ></LeafReliefHeader>

    <!-- Form -->
    <div class="processing-container">
      <div class="processing" v-if="isProcessing">
        <i class="fas fa-sync-alt fa-spin"></i>
      </div>
      <div class="processing" v-if="donation.isFailed">
        <h2>Unable to process donation</h2>
      </div>
      <Stepper
        :petitionSelector="petitionSelector"
        :donationSelector="donationSelector"
        :petitionHandle="petitionHandle"
        :programName="programName"
        :datalayerName="datalayerName"
        :thankYouText="thankYouText"
        :thankYouUrl="thankYouUrl"
        :getSocialText="getSocialText"
        :duplicateEmailMessage="duplicateEmailMessage"
        :raRgId="raRgId"
        :raSgId="raSgId"
        :edoId="edoId"
        :sendToIve="sendToIve"
        :iveSupplier="iveSupplier"
        :iveSource="iveSource"
        :isSpeciesForm="isSpeciesForm"
        v-if="!!product"
      ></Stepper>
    </div>

    <!-- Last signatures - Always present whatever step we are-->
    <div class="last-signatures" v-if="showNames">
      <div class="ls-title">{{ signatureTitle }}</div>
      <LastSignatures
        :campaignId="datalayerName"
        classes="ra-last-signatures"
        :signature="signature"
        :numberOfSignatures="numberOfSignatures"
      ></LastSignatures>
    </div>
  </div>
</template>

<script>
import LeafReliefHeader from "./Header";
import Stepper from "./Stepper.vue";
import LastSignatures from "./LastSignatures";
import slack from "../../utils/slack";

import {
  getParameterByName,
  getNameByDonationType,
  getNameByPaymentType,
  removeURLParameter,
  getUtmParams,
} from "../../helpers";
import gtm from "../../utils/gtm";
import { mapState } from "vuex";

export default {
  name: "LeafRelief",
  components: {
    LeafReliefHeader,
    Stepper,
    LastSignatures,
  },

  data() {
    return {
      defaultDonationType: null,
      isProcessing: false,
      showNames: false,
    };
  },
  computed: mapState({
    donation: (state) => state.donation.donation,
    product: (state) => state.product.product,
    supporter: (state) => state.leafRelief.details,
  }),
  props: {
    productId: {
      type: String,
      required: true,
    },
    datalayerName: {
      type: String,
      required: true,
    },

    programName: {
      type: String,
      required: true,
    },

    // IVE
    sendToIve: {
      type: Boolean,
      default: true,
    },
    iveSupplier: {
      type: String,
      default: "WWF-Aus-leaf-relief-phone-mandatory",
    },
    iveSource: {
      type: String,
      default: "LeafRelief",
    },

    // Last signatures
    numberOfSignatures: {
      type: Number,
      default: 3,
    },
    signatureTitle: {
      type: String,
      default: "Trees Planted",
    },
    signature: {
      type: String,
      default: null,
    },

    // Selectors
    petitionSelector: {
      type: String,
      default: "Plant a Tree",
    },
    donationSelector: {
      type: String,
      default: "Plant a Forest",
    },

    // Header
    targetTitle: {
      type: String,
      default: "Our target",
    },
    targetText: {
      type: String,
      default: "1 Million",
    },
    headerText: {
      type: String,
      default:
        "Join us in our quest to plant 2 billion trees and restore our unique habitat",
    },
    counterLimit: {
      type: Number,
      default: 100,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },

    // Details
    petitionHandle: {
      type: String,
      default: "Show your support and we'll plant a tree today",
    },
    duplicateEmailMessage: {
      type: String,
      default:
        "You can donate multiple times with the same email but you can only submit the petition once.",
    },

    // Thank You
    thankYouText: {
      type: String,
      default: "Thank you for helping repopulate our forests",
    },
    getSocialText: {
      type: String,
      default:
        "Share this opportunity with friends to let them plant their own seed",
    },
    thankYouUrl: {
      type: String,
      default:
        "https://wwf.org.au/get-involved/nowhere-to-go/thank-you-for-helping-koalas/",
    },

    // Thank You Popup
    raRgId: {
      type: String,
    },
    raSgId: {
      type: String,
    },
    edoId: {
      type: String,
    },

    // Change to B form for A/B testing
    isBForm: {
      type: Boolean,
      default: false,
    },

    // Change to species form (show electorate etc..)
    isSpeciesForm: {
      type: Boolean,
      default: false,
    },

    // Exit Intent Popup
    disableExitPopup: {
      type: Boolean,
      default: true,
    },
    exitPopupImage: {
      type: [String, Array],
    },
  },

  async created() {
    // Get source 
    let source = window.top.location.href;
    // If there is no utm params at the top url && persisted utm exists from the iframe url 
    if (getUtmParams(source).length === 0 && getUtmParams(window.location.href).length > 0) {
      // Add the persisted utm param 
      const url = window.top.location.href.split("?")[0];
      source = url + getUtmParams(window.location.href);
      
      // Add the rest of the params from top url back in if exists 
      const params = window.top.location.href.split("?")[1];
      if (params) {
        source += `&${params}`;
      }
    }
    
    // Fetch product informations
    // We can have query params to override the appeal and fund id
    const res = await Promise.all([
      this.$store.dispatch("getFeatureSwitch", "display-leaf-relief-names"),
      this.$store.dispatch("getProductById", {
        id: this.productId,
        source,
        appealid: getParameterByName("t"),
        fundid: getParameterByName("f"),
      }),
    ]);

    this.showNames = res[0];

    this.checkPaypalCallback();
    this.readQueryParams();
    this.setDonationType();

    this.$store.commit("enableBForm", this.isBForm);
  },
  mounted() {
    if (!this.disableExitPopup) {
      // For mobile/tablet devices to trigger exit popup on back button
      if (window.matchMedia("(max-width: 1024px)").matches) {
        window.history.pushState(
          {},
          window.document.title,
          window.location.href
        );
        window.onpopstate = () => {
          const hasDonated =
            window.sessionStorage.getItem("leafReliefActioned") === "true";
          if (!hasDonated) {
            this.openExitPopup();
          }
        };
      }

      // Event to display exit popup if a user has not donated on leaf relief during session
      document.addEventListener("mouseout", (e) => {
        const hasDonated =
          window.sessionStorage.getItem("leafReliefActioned") === "true";

        // Only trigger popup when mouse has left the document and exited towards address bar
        if (!hasDonated && !e.toElement && !e.relatedTarget && e.clientY < 10) {
          this.openExitPopup();
        }
      });
    }
  },
  methods: {
    async checkPaypalCallback() {
      // Wait for a second for recaptcha script to load
      await new Promise((resolve) => setTimeout(resolve, 1000));

      let timeout = null;
      try {
        // Check if there payment paypal pending to excecute
        const paymentId = getParameterByName("token", window.top.location.href);
        const payerId = getParameterByName("PayerID", window.top.location.href);
        if (payerId && paymentId) {
          console.log("Paypal Callback - Payment found");

          this.isProcessing = true;
          // Add a timeout for 30s
          timeout = setTimeout(this.paypalFailed, 30000);

          // Move to top of the form
          const el = document.getElementById("form-top");
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          }

          const paypalDonation = await this.$store.dispatch("paymentPaypal", {
            payerid: payerId,
            paymentid: paymentId,
          });
          // Clear the timeout if the payment was done within 30s
          clearTimeout(timeout);
          console.log("Paypal Callback - Donaion", paypalDonation);

          if (paypalDonation) {
            // Remove the query params from the URL
            let querylessUrl = removeURLParameter("token");
            querylessUrl = removeURLParameter("PayerID", querylessUrl);
            window.top.history.pushState({}, document.title, querylessUrl);

            gtm.pushPurchase(
              paypalDonation.key,
              paypalDonation.productname,
              paypalDonation.productid,
              paypalDonation.producttype,
              getNameByDonationType(paypalDonation.type),
              paypalDonation.amount,
              paypalDonation.postcode,
              getNameByPaymentType("wwf_paypal")
            );

            this.$store.dispatch("logFormPaySuccess", paypalDonation.key);

            // Set data and send to thank you page
            this.$store.commit("updateSelection", "donation");
            this.supporter.firstName = paypalDonation.firstname;
            this.supporter.lastName = paypalDonation.lastname;
            this.donation.amount = paypalDonation.amount;

            if (paypalDonation.extra.agreesDisplayName) {
              await this.$store.dispatch("saveSignature", {
                campaignId: this.datalayerName,
                firstName: this.supporter.firstName,
                lastName: this.supporter.lastName,
                isDonation: true,
              });
            }

            // Set session flag when donation complete to prevent exit popup from displaying
            window.top.sessionStorage.setItem("leafReliefActioned", "true");

            this.$store.dispatch("logLeafComplete");

            // Go to thank you page
            this.$store.commit("updateLeafRelief", {
              field: "currentStep",
              value: 2,
            });

            // Move to top of the form
            const el = document.getElementById("form-top");
            if (el) {
              el.scrollIntoView({ behavior: "smooth" });
            }
          }
        }
      } catch (err) {
        // execute timeout now as donation failed
        clearTimeout(timeout);
        this.paypalFailed();
        slack.alert(
          `Paypal error ${JSON.stringify(
            { ...err.body, url: err.url },
            null,
            4
          )}`
        );
        console.error("Paypal Callback - error", err);
      } finally {
        this.isProcessing = false;
      }
    },
    paypalFailed() {
      // Remove the Processing screen
      this.isProcessing = false;
      this.donation.isFailed = true;

      // Redirect users back to the page after 3s
      setTimeout(() => {
        this.donation.isFailed = false;
        window.top.location.href = window.top.location.href.split("?")[0];
      }, 4000);
    },
    readQueryParams() {
      // Check if there are some query string in URL to prefill the form, Query from donate widget
      const q = getParameterByName("q", window.top.location.href);
      if (q) {
        const paramsPrefill = "?" + window.atob(q);
        console.log("paramsPrefill", paramsPrefill);

        const amount = getParameterByName("amount", paramsPrefill);
        const email = getParameterByName("email", paramsPrefill);
        const firstname = getParameterByName("firstname", paramsPrefill);
        const lastname = getParameterByName("lastname", paramsPrefill);
        const salutation = getParameterByName("salutation", paramsPrefill);
        const wildcardCode = getParameterByName("wildcardCode", paramsPrefill);

        if (wildcardCode) {
          this.donation.packagecode = wildcardCode;
        }

        let dollarhandle = getParameterByName("dollarhandle", paramsPrefill)
          ? getParameterByName("dollarhandle", paramsPrefill).replace("$", "")
          : undefined;
        let dollarhandles = getParameterByName("dollarhandles", paramsPrefill);

        if (dollarhandles) {
          // get dollarhandles from query params
          dollarhandles = dollarhandles.replace(/[^0-9\. ]+/g, "").split(" ");
          const newDollarHandles = [];
          dollarhandles.forEach((v, index) => {
            // Check if there is a product dollar handles index
            if (this.product.dollarhandles.Single[index]) {
              newDollarHandles.push({
                amount: v,
                img: this.product.dollarhandles.Single[index].img,
                text: this.product.dollarhandles.Single[index].text,
              });
            }
          });
          this.product.dollarhandles.Single = newDollarHandles;
        }

        this.donation.dollarhandle = dollarhandle;
        this.donation.amount = amount;
        this.donation.title = salutation;

        this.supporter.email = email;
        this.supporter.firstName = firstname;
        this.supporter.lastName = lastname;
      }

      // Pre select Petition or donation (ST = Submission type)
      const st = getParameterByName("st", window.top.location.href);
      if (!!st && (st === "petition" || st === "donation")) {
        this.$store.commit("updateSelection", st);
      }
    },
    setDonationType() {
      // Set default donation type by query param
      const dt = getParameterByName("dt", window.top.location.href);
      if (dt && (dt === "Single" || dt === "Regular")) {
        this.defaultDonationType = dt;
      }

      // DONATION TYPE - Pre-select the doallar handle and the package (rule: first item on the list)
      // As we only have Single and Regular, always set Single as the first option
      const defaultTypeExists =
        this.product.donationtypes.filter((e) => e === this.defaultDonationType)
          .length === 1;
      let defaultType =
        defaultTypeExists && this.defaultDonationType
          ? this.defaultDonationType
          : this.product.appealcodesetting.defaultdonationtype;

      // Reverse alphabetical order
      this.product.donationtypes = this.product.donationtypes.sort((a, b) => {
        if (a === defaultType) {
          return -1;
        }
        if (b === defaultType) {
          return 1;
        }
        return 0;
      });

      this.donation.type = defaultType;
      const settings = this.product.donationsettings[defaultType];
      if (settings) {
        this.donation.campaignid = settings.campaignid;
        this.donation.fundid = settings.fundid;
      }

      // DOLLAR HANDLE - Select first element
      const handles = this.product.dollarhandles[defaultType];
      if (handles) {
        this.donation.amount = handles[0].amount;
        this.donation.dollarhandle = handles[0].amount;
      }
    },
    openExitPopup() {
      if (!this.$store.getters.content.isOpen) {
        this.$store.commit("setPopupContent", {
          component: "leaf-relief-intent-popup",
          componentparams: {
            image: this.exitPopupImage,
          },
        });
        this.$store.commit("openCustomContentPopup", true);

        // Log to Amplitude
        this.$store.dispatch("logPopup");
      }
    },
  },
};
</script>

<style scoped>
.last-signatures {
  padding: var(--lrPadding);
  padding-top: 0;
}

@media only screen and (max-width: 64em) {
  .donation-form.wwf-form {
    padding: 0px;
  }
}

.last-signatures .ls-title {
  color: var(--wwfRegenerateAustralia);
  font-weight: 600;
}

.processing-container {
  position: relative;
}

.processing {
  z-index: 11;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  font-size: 40px;
  color: var(--wwfWhite);
  background-color: #00000075;
}
</style>


// WEBPACK FOOTER //
// LeafRelief.vue?7ae60370